import { Link } from "react-router-dom";
import { Leaf, Mail, LifeBuoy } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LanguageSelector } from "@/components/LanguageSelector";
import { LegalLinks } from "@/components/LegalLinks";
import { SocialLinks } from "@/components/SocialLinks";
import { Button } from "@/components/ui/button";
import { supportFor, SUPPORT_EMAIL } from "@/i18n/support";
import { PRIVACY_PUBLISHED } from "@/i18n/legal";

export default function Support() {
  const { lang } = useLang();
  const s = supportFor(lang);

  return (
    <div className="min-h-screen bg-background" data-testid="support-page">
      <header className="max-w-3xl mx-auto px-5 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="support-home-link">
          <span className="grid place-items-center h-9 w-9 rounded-2xl bg-primary text-primary-foreground"><Leaf className="h-5 w-5" /></span>
          <span className="font-serif text-xl font-semibold">CHENIA</span>
        </Link>
        <LanguageSelector />
      </header>

      <main className="max-w-3xl mx-auto px-5 pb-16">
        <div className="flex items-center gap-3 mt-8">
          <span className="grid place-items-center h-11 w-11 rounded-2xl bg-chenia-rosesoft text-primary"><LifeBuoy className="h-5 w-5" /></span>
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="support-title">{s.title}</h1>
        </div>
        <p className="mt-4 text-base leading-relaxed">{s.intro}</p>

        <section className="mt-6 chenia-card p-5 sm:p-6" data-testid="support-email-card">
          <h2 className="font-serif text-lg md:text-xl font-semibold tracking-tight">{s.email_h}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{s.email_p}</p>
          <a href={`mailto:${SUPPORT_EMAIL}`} className="mt-3 inline-block font-semibold text-primary underline underline-offset-4 break-all text-base" data-testid="support-email-link">{SUPPORT_EMAIL}</a>
          <div className="mt-4">
            <Button asChild className="rounded-full" data-testid="support-email-button">
              <a href={`mailto:${SUPPORT_EMAIL}`}><Mail className="h-4 w-4 mr-2" />{s.email_btn}</a>
            </Button>
          </div>
        </section>

        <section className="mt-4 chenia-card p-5 sm:p-6" data-testid="support-tips-card">
          <h2 className="font-serif text-lg md:text-xl font-semibold tracking-tight">{s.tips_h}</h2>
          <ul className="mt-3 space-y-2 text-sm leading-relaxed list-disc pl-5">{s.tips.map((t, i) => <li key={i}>{t}</li>)}</ul>
        </section>

        <section className="mt-4 chenia-card p-5 sm:p-6" data-testid="support-topics-card">
          <h2 className="font-serif text-lg md:text-xl font-semibold tracking-tight">{s.topics_h}</h2>
          <dl className="mt-3 space-y-4">
            {s.topics.map((tp, i) => (
              <div key={i}>
                <dt className="text-sm font-semibold">{tp.h}</dt>
                <dd className="mt-1 text-sm leading-relaxed text-muted-foreground">{tp.p}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm">
            <Link to="/delete-account" className="text-primary underline underline-offset-4" data-testid="support-delete-link">{s.delete_link}</Link>
            {PRIVACY_PUBLISHED && <Link to="/privacy" className="text-primary underline underline-offset-4" data-testid="support-privacy-link">{s.privacy_link}</Link>}
          </div>
        </section>

        <footer className="mt-10 pt-6 border-t border-border flex flex-wrap items-center justify-between gap-3">
          <Link to="/" className="text-sm text-primary underline underline-offset-4" data-testid="support-back-home">{s.back_home}</Link>
          <SocialLinks />
        </footer>
        <LegalLinks className="mt-3" testIdPrefix="support-footer" />
      </main>
    </div>
  );
}
